<!-- eslint-disable -->
<template>
  <div class="pa-4">
    <!-- Filters -->
    <v-row class="mb-4">
      <v-col cols="12" md="4">
        <v-text-field
          v-model="searchQuery"
          :placeholder="$t('admin.searchUsers')"
          prepend-inner-icon="mdi-magnify"
          variant="outlined"
          density="compact"
          clearable
          @update:model-value="debouncedSearch"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="3">
        <v-select
          v-model="statusFilter"
          :items="statusOptions"
          :label="$t('admin.filterByStatus')"
          variant="outlined"
          density="compact"
          clearable
          @update:model-value="loadUsers"
        ></v-select>
      </v-col>
    </v-row>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-8">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>

    <!-- Users Table -->
    <v-table v-else>
      <thead>
        <tr>
          <th>{{ $t('admin.user') }}</th>
          <th>{{ $t('admin.email') }}</th>
          <th>{{ $t('admin.status') }}</th>
          <th>{{ $t('admin.role') }}</th>
          <th>{{ $t('admin.posts') }}</th>
          <th>{{ $t('admin.joined') }}</th>
          <th>{{ $t('admin.actions') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="user in users" :key="user.userId">
          <td>
            <div class="d-flex align-center">
              <v-avatar size="32" class="mr-2" :color="user.avatarUrl ? undefined : 'primary'">
                <v-img v-if="user.avatarUrl" :src="user.avatarUrl"></v-img>
                <span v-else class="text-white text-caption">{{ user.username?.charAt(0).toUpperCase() }}</span>
              </v-avatar>
              <div>
                <div class="font-weight-medium">{{ user.displayName || user.username }}</div>
                <div class="text-caption text-grey">@{{ user.username }}</div>
              </div>
            </div>
          </td>
          <td>{{ user.email }}</td>
          <td>
            <v-chip
              :color="statusColor(user.status)"
              size="small"
            >
              {{ $t(`admin.statusLabels.${user.status}`) }}
            </v-chip>
          </td>
          <td>
            <v-chip
              v-if="user.isAdmin"
              color="primary"
              size="small"
            >
              Admin
            </v-chip>
            <span v-else class="text-grey">{{ $t('admin.member') }}</span>
          </td>
          <td>{{ user.postCount }}</td>
          <td>{{ formatDate(user.created_at) }}</td>
          <td>
            <v-menu>
              <template v-slot:activator="{ props }">
                <v-btn icon="mdi-dots-vertical" variant="text" size="small" v-bind="props"></v-btn>
              </template>
              <v-list density="compact">
                <v-list-item @click="viewProfile(user)">
                  <template v-slot:prepend>
                    <v-icon>mdi-account</v-icon>
                  </template>
                  <v-list-item-title>{{ $t('admin.viewProfile') }}</v-list-item-title>
                </v-list-item>
                <v-divider></v-divider>
                <v-list-item 
                  v-if="user.status !== 'active'" 
                  @click="updateStatus(user, 'active')"
                >
                  <template v-slot:prepend>
                    <v-icon color="success">mdi-check-circle</v-icon>
                  </template>
                  <v-list-item-title>{{ $t('admin.activate') }}</v-list-item-title>
                </v-list-item>
                <v-list-item 
                  v-if="user.status !== 'inactive'" 
                  @click="updateStatus(user, 'inactive')"
                >
                  <template v-slot:prepend>
                    <v-icon color="grey">mdi-pause-circle</v-icon>
                  </template>
                  <v-list-item-title>{{ $t('admin.deactivate') }}</v-list-item-title>
                </v-list-item>
                <v-list-item 
                  v-if="user.status !== 'blocked'" 
                  @click="updateStatus(user, 'blocked')"
                >
                  <template v-slot:prepend>
                    <v-icon color="error">mdi-block-helper</v-icon>
                  </template>
                  <v-list-item-title>{{ $t('admin.block') }}</v-list-item-title>
                </v-list-item>
                <v-divider></v-divider>
                <v-list-item 
                  v-if="!user.isAdmin" 
                  @click="toggleAdmin(user, true)"
                >
                  <template v-slot:prepend>
                    <v-icon color="primary">mdi-shield-account</v-icon>
                  </template>
                  <v-list-item-title>{{ $t('admin.makeAdmin') }}</v-list-item-title>
                </v-list-item>
                <v-list-item 
                  v-if="user.isAdmin" 
                  @click="toggleAdmin(user, false)"
                >
                  <template v-slot:prepend>
                    <v-icon>mdi-shield-off</v-icon>
                  </template>
                  <v-list-item-title>{{ $t('admin.removeAdmin') }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </td>
        </tr>
      </tbody>
    </v-table>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="d-flex justify-center mt-4">
      <v-pagination
        v-model="currentPage"
        :length="totalPages"
        :total-visible="7"
        @update:model-value="loadUsers"
      ></v-pagination>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable */
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import api from '../../services/api'

const router = useRouter()
const { t, locale } = useI18n()

const users = ref([])
const loading = ref(false)
const searchQuery = ref('')
const statusFilter = ref(null)
const currentPage = ref(1)
const totalPages = ref(1)
const perPage = 20

const statusOptions = [
  { value: 'active', title: t('admin.statusLabels.active') },
  { value: 'inactive', title: t('admin.statusLabels.inactive') },
  { value: 'blocked', title: t('admin.statusLabels.blocked') }
]

let searchTimeout = null

const debouncedSearch = () => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    currentPage.value = 1
    loadUsers()
  }, 300)
}

const loadUsers = async () => {
  loading.value = true
  try {
    const params = {
      page: currentPage.value,
      limit: perPage
    }
    
    if (searchQuery.value) {
      params.search = searchQuery.value
    }
    if (statusFilter.value) {
      params.status = statusFilter.value
    }
    
    const response = await api.get('/admin/users', { params })
    users.value = response.data.users || []
    totalPages.value = response.data.totalPages || 1
  } catch (error) {
    console.error('Failed to load users:', error)
  } finally {
    loading.value = false
  }
}

const statusColor = (status) => {
  switch (status) {
    case 'active': return 'success'
    case 'inactive': return 'grey'
    case 'blocked': return 'error'
    default: return 'grey'
  }
}

const formatDate = (dateStr) => {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString(locale.value)
}

const viewProfile = (user) => {
  router.push(`/profile/${user.username}`)
}

const updateStatus = async (user, status) => {
  try {
    await api.put('/admin/users', { userId: user.userId, status })
    user.status = status
  } catch (error) {
    console.error('Failed to update user status:', error)
    alert(error.response?.data?.error || 'Failed to update')
  }
}

const toggleAdmin = async (user, isAdmin) => {
  try {
    await api.put('/admin/users', { userId: user.userId, isAdmin })
    user.isAdmin = isAdmin
  } catch (error) {
    console.error('Failed to toggle admin:', error)
    alert(error.response?.data?.error || 'Failed to update')
  }
}

onMounted(() => {
  loadUsers()
})
</script>